import type {
  acceptAgentMessageMutation,
  acceptAgentMessageMutationVariables,
  AgentsInput,
  AskAutodorCreateSavedPromptInput,
  AskAutodorDeleteSavedPromptInput,
  AskAutodorSavedPromptsNewTagsInput,
  CasesArgs,
  CasesInput,
  createAgentDemoMutation,
  createAgentDemoMutationVariables,
  createAgentMessageRevisionMutation,
  createAgentMessageRevisionMutationVariables,
  createAskAutodorSavedPromptMutation,
  CreateCaseInput,
  createCaseMutation,
  CreateThreadInput,
  createThreadMutation,
  deleteAskAutodorSavedPromptMutation,
  deleteCaseMutation,
  getAgentQuery,
  getAgentsQuery,
  getAgentTypesQuery,
  getAskAutodorSavedPromptsQuery,
  getCaseQuery,
  getCasesConnectionQuery,
  getCasesConnectionQueryVariables,
  getCasesQuery,
  getClaimCasesQuery,
  getThreadQuery,
  getThreadQueryVariables,
  getThreadsQuery,
  MoveCasesToQueueInput,
  moveCasesToQueueMutation,
  PromptInput,
  promptMutation,
  PromptResponseRatingType,
  ratePromptResponseMutation,
  resetAskAutodorSavedPromptMutation,
  ThreadsInput,
  unsetAgentMessageUserValidationMutation,
  unsetAgentMessageUserValidationMutationVariables,
  UpdateCaseInput,
  updateCaseMutation,
} from "../../remote/gql/graphql";
import type { ClientReturnType } from "../types";
import type { AlaffiaClientModuleArgs } from "./modules";

import { acceptAgentMessageMutationDoc } from "../../remote/mutations/acceptAgentMessage.gql";
import { createAgentDemoMutationDoc } from "../../remote/mutations/createAgentDemo.gql";
import { createAgentMessageRevisionMutationDoc } from "../../remote/mutations/createAgentMessageRevision.gql";
import { createAskAutodorSavedPromptMutationDoc } from "../../remote/mutations/createAskAutodorSavedPrompt.gql";
import { createCaseMutationDoc } from "../../remote/mutations/createCase.gql";
import { createThreadMutationDoc } from "../../remote/mutations/createThread.gql";
import { deleteAskAutodorSavedPromptMutationDoc } from "../../remote/mutations/deleteAskAutodorSavedPrompt.gql";
import { deleteCaseMutationDoc } from "../../remote/mutations/deleteCase.gql";
import { moveCasesToQueueMutationDoc } from "../../remote/mutations/moveCasesToQueue.gql";
import { promptMutationDoc } from "../../remote/mutations/prompt.gql";
import { ratePromptResponseMutationDoc } from "../../remote/mutations/ratePromptResponse.gql";
import { resetAskAutodorSavedPromptMutationDoc } from "../../remote/mutations/resetAskAutodorSavedPrompt.gql";
import { unsetAgentMessageUserValidationMutationDoc } from "../../remote/mutations/unsetAgentMessageUserValidation.gql";
import { updateCaseMutationDoc } from "../../remote/mutations/updateCase.gql";
import { getAgentQueryDoc } from "../../remote/queries/getAgent.gql";
import { getAgentsQueryDoc } from "../../remote/queries/getAgents.gql";
import { getAgentTypesQueryDoc } from "../../remote/queries/getAgentTypes.gql";
import { getAskAutodorSavedPromptsQueryDoc } from "../../remote/queries/getAskAutodorSavedPrompts.gql";
import { getCaseQueryDoc } from "../../remote/queries/getCase.gql";
import { getCasesQueryDoc } from "../../remote/queries/getCases.gql";
import { getCasesConnectionQueryDoc } from "../../remote/queries/getCasesConnection.gql";
import { getClaimCasesQueryDoc } from "../../remote/queries/getClaimCases.gql";
import { getThreadQueryDoc } from "../../remote/queries/getThread.gql";
import { getThreadsQueryDoc } from "../../remote/queries/getThreads.gql";
import { AlaffiaClientModule } from "./modules";

type CasesModuleArgs = AlaffiaClientModuleArgs;

export class CasesModule extends AlaffiaClientModule {
  constructor(args: CasesModuleArgs) {
    super(args);
  }

  getAllCases = async (input: {
    input?: CasesInput;
  }): ClientReturnType<getCasesQuery["cases"]> => {
    const result = await this.runGqlQuery(
      getCasesQueryDoc,
      { input: input?.input ?? {} },
      "getAllCases",
    );

    return {
      data: result.data?.cases ?? null,
      error: result.error ?? null,
    };
  };

  getCasesConnection = async (
    input: Pick<CasesArgs, "filterGraph" | "queueId">,
    args?: Omit<
      getCasesConnectionQueryVariables["args"],
      "filterGraph" | "queueId"
    >,
  ): ClientReturnType<getCasesConnectionQuery["casesConnection"]> => {
    const result = await this.runGqlQuery(
      getCasesConnectionQueryDoc,
      {
        args: {
          ...args,
          filterGraph: input.filterGraph,
          queueId: input.queueId,
        },
      },
      "getCasesConnection",
    );
    return {
      data: result.data?.casesConnection ?? null,
      error: result.error ?? null,
    };
  };

  getClaimCases = async (input: {
    claimId: string;
  }): ClientReturnType<getClaimCasesQuery["claimCases"]> => {
    const result = await this.runGqlQuery(
      getClaimCasesQueryDoc,
      { claimId: input.claimId },
      "getClaimCases",
    );

    return {
      data: result.data?.claimCases ?? null,
      error: result.error ?? null,
    };
  };

  getCase = async (input: {
    id: string;
    withClaim?: boolean;
  }): ClientReturnType<getCaseQuery["case"]> => {
    const result = await this.runGqlQuery(
      getCaseQueryDoc,
      { id: input.id, withClaim: input.withClaim ?? false },
      "getCase",
    );
    return {
      data: result.data?.case ?? null,
      error: result.error ?? null,
    };
  };

  createCase = async (input: {
    input: CreateCaseInput;
  }): ClientReturnType<createCaseMutation["createCase"]> => {
    const result = await this.runGqlMutation(
      createCaseMutationDoc,
      { input: input.input },
      "createCase",
    );

    return {
      data: result.data?.createCase ?? null,
      error: result.error ?? null,
    };
  };

  deleteCase = async (input: {
    id: string;
  }): ClientReturnType<deleteCaseMutation["deleteCase"]> => {
    const result = await this.runGqlMutation(
      deleteCaseMutationDoc,
      { id: input.id },
      "deleteCase",
    );

    return {
      data: result.data?.deleteCase ?? null,
      error: result.error ?? null,
    };
  };

  updateCase = async ({
    id,
    updateInput,
  }: {
    id: string;
    updateInput: UpdateCaseInput;
  }): ClientReturnType<updateCaseMutation["updateCase"]> => {
    const result = await this.runGqlMutation(
      updateCaseMutationDoc,
      {
        id,
        input: {
          name: updateInput.name,
          fileIds: updateInput.fileIds,
          queueId: updateInput.queueId,
          status: updateInput.status,
        },
      },
      "updateCase",
    );

    return {
      data: result.data?.updateCase ?? null,
      error: result.error ?? null,
    };
  };

  moveCasesToQueue = async ({
    input,
  }: {
    input: MoveCasesToQueueInput;
  }): ClientReturnType<moveCasesToQueueMutation["moveCasesToQueue"]> => {
    const result = await this.runGqlMutation(
      moveCasesToQueueMutationDoc,
      { input },
      "moveCasesToQueue",
    );

    return {
      data: result.data?.moveCasesToQueue ?? null,
      error: result.error ?? null,
    };
  };

  createThread = async (input: {
    caseId: string;
    input: CreateThreadInput;
  }): ClientReturnType<createThreadMutation["createThread"]> => {
    const result = await this.runGqlMutation(
      createThreadMutationDoc,
      { caseId: input.caseId, input: input.input },
      "createThread",
    );

    return {
      data: result.data?.createThread ?? null,
      error: result.error ?? null,
    };
  };

  getAllSavedPrompts = async (): ClientReturnType<
    getAskAutodorSavedPromptsQuery["askAutodorGetSavedPrompts"]
  > => {
    const result = await this.runGqlQuery(
      getAskAutodorSavedPromptsQueryDoc,
      { input: { tags: [] } },
      "getAllSavedPrompts",
    );

    return {
      data: result.data?.askAutodorGetSavedPrompts ?? null,
      error: result.error ?? null,
    };
  };

  createSavedPrompt = async (
    input: AskAutodorCreateSavedPromptInput,
  ): ClientReturnType<
    createAskAutodorSavedPromptMutation["askAutodorCreateSavedPrompt"]
  > => {
    const result = await this.runGqlMutation(
      createAskAutodorSavedPromptMutationDoc,
      { input },
      "createSavedPrompt",
    );

    return {
      data: result.data?.askAutodorCreateSavedPrompt ?? null,
      error: result.error ?? null,
    };
  };

  deleteSavedPrompt = async (input: {
    input: AskAutodorDeleteSavedPromptInput;
  }): ClientReturnType<
    deleteAskAutodorSavedPromptMutation["askAutodorDeleteSavedPrompt"]
  > => {
    const result = await this.runGqlMutation(
      deleteAskAutodorSavedPromptMutationDoc,
      { input: input.input },
      "deleteAskAutodorSavedPrompt",
    );

    return {
      data: result.data?.askAutodorDeleteSavedPrompt ?? null,
      error: result.error ?? null,
    };
  };

  resetSavedPrompt = async (input: {
    input: AskAutodorSavedPromptsNewTagsInput;
  }): ClientReturnType<
    resetAskAutodorSavedPromptMutation["askAutodorResetSavedPromptTags"]
  > => {
    const result = await this.runGqlMutation(
      resetAskAutodorSavedPromptMutationDoc,
      { input: input.input },
      "resetAskAutodorSavedPrompt",
    );

    return {
      data: result.data?.askAutodorResetSavedPromptTags ?? null,
      error: result.error ?? null,
    };
  };

  getThreads = async (input: {
    input: ThreadsInput;
  }): ClientReturnType<getThreadsQuery["threads"]> => {
    const result = await this.runGqlQuery(
      getThreadsQueryDoc,
      { input: input.input },
      "getThreads",
    );

    return {
      data: result.data?.threads ?? null,
      error: result.error ?? null,
    };
  };

  getThread = async (
    { id }: Pick<getThreadQueryVariables, "id">,
    messagesArgs?: getThreadQueryVariables["messagesArgs"],
  ): ClientReturnType<getThreadQuery["thread"]> => {
    const result = await this.runGqlQuery(
      getThreadQueryDoc,
      { id, messagesArgs },
      "getThread",
    );

    return {
      data: result.data?.thread ?? null,
      error: result.error ?? null,
    };
  };

  prompt = async (input: {
    input: PromptInput;
  }): ClientReturnType<promptMutation["prompt"]> => {
    const result = await this.runGqlMutation(
      promptMutationDoc,
      { input: input.input },
      "prompt",
    );

    return {
      data: result.data?.prompt ?? null,
      error: result.error ?? null,
    };
  };

  ratePromptResponse = async (input: {
    promptResponseId: string;
    rating: PromptResponseRatingType | null;
  }): ClientReturnType<ratePromptResponseMutation["ratePromptResponse"]> => {
    const result = await this.runGqlMutation(
      ratePromptResponseMutationDoc,
      { promptResponseId: input.promptResponseId, rating: input.rating },
      "ratePromptResponse",
    );

    return {
      data: result.data?.ratePromptResponse ?? null,
      error: result.error ?? null,
    };
  };

  getAgents = async (input: {
    input: AgentsInput;
  }): ClientReturnType<getAgentsQuery["agents"]> => {
    const result = await this.runGqlQuery(
      getAgentsQueryDoc,
      { input: input.input },
      "getAgents",
    );

    return {
      data: result.data?.agents ?? null,
      error: result.error ?? null,
    };
  };

  getAgent = async (input: {
    id: string;
  }): ClientReturnType<getAgentQuery["agent"]> => {
    const result = await this.runGqlQuery(
      getAgentQueryDoc,
      { id: input.id },
      "getAgent",
    );

    return {
      data: result.data?.agent ?? null,
      error: result.error ?? null,
    };
  };

  getAgentTypes = async (): ClientReturnType<
    getAgentTypesQuery["agentTypes"]
  > => {
    const result = await this.runGqlQuery(
      getAgentTypesQueryDoc,
      {},
      "getAgentTypes",
    );

    return {
      data: result?.data?.agentTypes ?? null,
      error: result?.error ?? null,
    };
  };

  createAgentDemo = async ({
    input,
  }: createAgentDemoMutationVariables): ClientReturnType<
    createAgentDemoMutation["createAgentDemo"]
  > => {
    const result = await this.runGqlMutation(
      createAgentDemoMutationDoc,
      { input },
      "createAgentDemo",
    );

    return {
      data: result?.data?.createAgentDemo ?? null,
      error: result?.error ?? null,
    };
  };

  createAgentMessageRevision = async ({
    agentMessageId,
    input,
  }: createAgentMessageRevisionMutationVariables): ClientReturnType<
    createAgentMessageRevisionMutation["createAgentMessageRevision"]
  > => {
    const result = await this.runGqlMutation(
      createAgentMessageRevisionMutationDoc,
      { agentMessageId, input },
      "createAgentMessageRevision",
    );

    return {
      data: result?.data?.createAgentMessageRevision ?? null,
      error: result?.error ?? null,
    };
  };

  acceptAgentMessage = async ({
    agentMessageId,
    revisionId,
  }: acceptAgentMessageMutationVariables): ClientReturnType<
    acceptAgentMessageMutation["acceptAgentMessage"]
  > => {
    const result = await this.runGqlMutation(
      acceptAgentMessageMutationDoc,
      { agentMessageId, revisionId },
      "accentAgentMessage",
    );

    return {
      data: result?.data?.acceptAgentMessage ?? null,
      error: result?.error ?? null,
    };
  };

  unsetAgentMessageUserValidation = async ({
    agentMessageId,
    revisionId,
  }: unsetAgentMessageUserValidationMutationVariables): ClientReturnType<
    unsetAgentMessageUserValidationMutation["unsetAgentMessageUserValidation"]
  > => {
    const result = await this.runGqlMutation(
      unsetAgentMessageUserValidationMutationDoc,
      { agentMessageId, revisionId },
      "unsetAgentMessageUserValidation",
    );

    return {
      data: result?.data?.unsetAgentMessageUserValidation ?? null,
      error: result?.error ?? null,
    };
  };
}
